import { EntityViewKanbanColumn } from '@jms/model';
import { KanbanValueSetOption } from '../../model/kanban.value.set';
import type { KanbanRuleColumn } from './kanban.buckets';

/**
 * Turns a view's column definitions into the value-set options the bucketer renders.
 *
 * A view column is a COMBINATION of values, so this is where "one column standing for many statuses"
 * becomes a single option carrying its members as aliases. A rule column resolves against the LOADED
 * value set rather than a frozen key list, which is the whole point of the rule: a status a tenant adds
 * tomorrow carries the same group value and joins its column without anyone editing the view.
 *
 * A value covered by no column is simply absent from the result and therefore not rendered — omission
 * is how a view drops a status from the board.
 */
export function resolveKanbanColumnOptions(columns: EntityViewKanbanColumn[] | null | undefined, valueSet: KanbanValueSetOption[] | null | undefined): KanbanValueSetOption[] | null {

  if (!columns?.length) return valueSet?.length ? valueSet : null;
  if (!valueSet?.length) return null;

  const byKey = new Map(valueSet.map(option => [option.key, option]));
  const resolved: KanbanValueSetOption[] = [];

  for (const column of columns) {
    if (column?.rule) {
      resolved.push(...resolveRuleColumns(column, valueSet));
      continue;
    }

    const option = resolveValuesColumn(column, byKey);
    if (option) resolved.push(option);
  }

  return resolved.length ? resolved : null;
}

/**
 * The view's rule columns, in the shape the bucketer places uncovered rows with.
 *
 * The bucket key must match what `resolveRuleColumns` emits — the column's own id for a combining
 * rule — or a row read off its embedded attribute would be handed to a bucket that does not exist.
 */
export function resolveKanbanRuleColumns(columns: EntityViewKanbanColumn[] | null | undefined): KanbanRuleColumn[] {

  return (columns ?? [])
    .filter(column => !!column?.rule)
    .map(column => ({ key: column.id, groupBy: column.rule!.groupBy, value: String(column.rule!.value), expand: column.rule!.expand }));
}

/**
 * A rule column, expanded or combined.
 *
 * `expand` emits the group's members as their own columns in value-set order — the open queue is worked
 * status by status — where the default folds them into one column the operator only watches.
 */
function resolveRuleColumns(column: EntityViewKanbanColumn, valueSet: KanbanValueSetOption[]): KanbanValueSetOption[] {

  const rule = column.rule!;
  const members = valueSet.filter(option => readGroupValue(option, rule.groupBy) === rule.value);

  if (rule.expand) return members;

  return [{
    key: column.id,
    name: column.label ?? `${rule.value}: All`,
    // The group's colour, taken from a member rather than from the column field — a group of statuses
    // has no swatch of its own, and borrowing the first member's is the only honest answer available
    // without the board knowing what kind of field this is.
    color: column.color ?? members.find(member => !!member.color)?.color ?? null,
    sortOrder: members.find(member => member.sortOrder != null)?.sortOrder ?? null,
    keys: members.map(member => member.key).filter(key => !!key),
    data: members[0]?.data ?? null,
  }];
}

/** An explicit combination. A single named value keeps that value's own label and colour. */
function resolveValuesColumn(column: EntityViewKanbanColumn, byKey: Map<string, KanbanValueSetOption>): KanbanValueSetOption | null {

  const members = (column?.values ?? []).map(value => byKey.get(value)).filter(Boolean) as KanbanValueSetOption[];
  if (!members.length) return null;

  const single = members.length === 1 ? members[0] : null;

  return {
    key: column.id,
    name: column.label ?? single?.name ?? column.id,
    color: column.color ?? single?.color ?? members.find(member => !!member.color)?.color ?? null,
    sortOrder: single?.sortOrder ?? members.find(member => member.sortOrder != null)?.sortOrder ?? null,
    keys: members.map(member => member.key),
    data: single?.data ?? members[0]?.data ?? null,
  };
}

/** Reads a grouping attribute off an option's source record (`option.data.systemStatus`, say). */
export function readGroupValue(option: KanbanValueSetOption, groupBy: string): string | null {

  const source = option?.data ?? option;
  const value = groupBy.split('.').reduce<any>((acc, part) => acc?.[part], source);
  return value == null ? null : String(value);
}
